import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  alpha,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  CalendarMonth as CalendarIcon,
  People as PeopleIcon,
  Store as StoreIcon,
  Celebration as CelebrationIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';
import { authService } from '../../services/firebase/auth.service';

interface FeatureCard {
  icon: React.ReactNode;
  title: string;
  description: string;
  color: string;
}

const features: FeatureCard[] = [
  {
    icon: <DashboardIcon />,
    title: 'Dashboard',
    description: 'Get a quick overview of schedules, coverage, and team status',
    color: '#6366F1',
  },
  {
    icon: <CalendarIcon />,
    title: 'Schedules',
    description: 'Create and manage shift schedules for your team',
    color: '#8B5CF6',
  },
  {
    icon: <PeopleIcon />,
    title: 'Employees',
    description: 'Manage team members and track their availability',
    color: '#EC4899',
  },
  {
    icon: <StoreIcon />,
    title: 'Shops',
    description: 'Configure shop hours and coverage requirements',
    color: '#10B981',
  },
];

interface StaffOnboardingModalProps {
  open: boolean;
  onComplete: () => void;
}

export default function StaffOnboardingModal({ open, onComplete }: StaffOnboardingModalProps) {
  const { user, updateUserProfile } = useAuth();
  const { organization } = useOrganization();
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  const handleComplete = async () => {
    if (!user?.uid) return;

    try {
      setIsCompleting(true);

      if (dontShowAgain) {
        await authService.completeOnboarding(user.uid, 'staff');
        await updateUserProfile({ onboardingCompleted: true, onboardingType: 'staff' });
      }

      onComplete();
    } catch (error) {
      console.error('Error completing onboarding:', error);
      onComplete();
    } finally {
      setIsCompleting(false);
    }
  };

  const roleDisplay = user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Team Member';

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
            p: 4,
            textAlign: 'center',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Background decoration */}
          <Box
            sx={{
              position: 'absolute',
              top: -30,
              right: -30,
              width: 150,
              height: 150,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.1)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -20,
              left: -20,
              width: 100,
              height: 100,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.1)',
            }}
          />

          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <CelebrationIcon sx={{ fontSize: 36 }} />
          </Box>

          <Typography variant="h4" fontWeight={700} gutterBottom>
            Welcome to {organization?.name}!
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            You've joined as <strong>{roleDisplay}</strong>
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ p: 4 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Here's what you can do
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Explore these features to manage schedules and your team effectively
          </Typography>

          <Grid container spacing={2}>
            {features.map((feature) => (
              <Grid item xs={6} key={feature.title}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    height: '100%',
                    borderRadius: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: feature.color,
                      bgcolor: alpha(feature.color, 0.04),
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      bgcolor: alpha(feature.color, 0.1),
                      color: feature.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 1.5,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    {feature.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Don't show again */}
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                sx={{
                  color: '#6366F1',
                  '&.Mui-checked': {
                    color: '#6366F1',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Don't show this again
              </Typography>
            }
            sx={{ mt: 3 }}
          />

          {/* CTA Button */}
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleComplete}
            disabled={isCompleting}
            endIcon={<ArrowForwardIcon />}
            sx={{
              mt: 2,
              py: 1.5,
              background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
            }}
          >
            Explore Dashboard
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
